import { useCallback, useEffect, useRef, useState } from 'react'
import { ALL_TIME_SPAN, fromDay, presetSpan, toDay, type DaySpan, type RangePreset } from './DateRangeButton'
import { DEFAULT_ACADEMIC, anchoredSpan, isRangeAnchor, type AcademicCalendar, type RangeAnchor } from './periods'
import { istDayOf } from '../lib/ist'

/**
 * A screen's date range, kept in the hash query (`#/school/subscription?range=30d`)
 * so it survives a reload and travels in a link.
 *
 * One `range` value, four spellings:
 *   - `all`                     → "All time" (no `from`/`to` sent)
 *   - `30d`                     → the last 30 days ENDING TODAY — re-read each
 *                                 day, so a bookmarked "Last 30 days" stays the
 *                                 last 30 days rather than freezing on the day
 *                                 it was saved
 *   - `wtd` `mtd` `term` `ay`   → week / month to date, this term, this
 *                                 academic year — re-read each day the same way
 *                                 (`periods.ts`), so "This term" saved in July
 *                                 opens on the November term in December
 *   - `2026-09-01..2026-09-30`  → a fixed custom span
 *
 * Absent means the screen's default, and choosing the default removes the value,
 * so a screen left on its default keeps a clean URL.
 */

/** `days` back from today (inclusive), `'all'`, or a calendar anchor. */
export type RangeDefault = number | 'all' | RangeAnchor

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
const MAX_PRESET_DAYS = 3660

function validDay(s: string): boolean {
  return DAY_RE.test(s) && toDay(fromDay(s)) === s
}

function spanDays(from: string, to: string): number {
  return Math.round((fromDay(to).getTime() - fromDay(from).getTime()) / 86_400_000) + 1
}

/** The span a default stands for, today. */
export function defaultSpan(d: RangeDefault, now: Date = new Date(), cal: AcademicCalendar = DEFAULT_ACADEMIC): DaySpan {
  if (d === 'all') return ALL_TIME_SPAN
  if (isRangeAnchor(d)) return anchoredSpan(d, now, cal)
  return presetSpan(d, now)
}

/**
 * A span as its hash spelling. A span chosen from an anchored preset — and
 * still that preset's span today — is stored as the anchor (`mtd`); a span
 * ending today (TODAY IN INDIA, the day `presetSpan` ends on) is stored
 * relative (`30d`); anything else literally.
 */
export function encodeRange(r: DaySpan, now: Date = new Date(), preset?: RangePreset, cal: AcademicCalendar = DEFAULT_ACADEMIC): string {
  if (r.from === '' && r.to === '') return 'all'
  if (preset?.anchor !== undefined) {
    const s = anchoredSpan(preset.anchor, now, cal)
    if (s.from === r.from && s.to === r.to) return preset.anchor
  }
  if (r.to === istDayOf(now)) return `${String(spanDays(r.from, r.to))}d`
  return `${r.from}..${r.to}`
}

/**
 * A hash spelling back to a span, or null when it is not one (a hand-edited
 * URL falls back to the screen's default rather than erroring). A backwards
 * custom pair is swapped; a span longer than `maxDays`, or "all" where the
 * screen needs bounds, is refused — an anchor too, when TODAY's span of it is
 * longer than the screen allows.
 */
export function decodeRange(raw: string | null, now: Date = new Date(), maxDays?: number, cal: AcademicCalendar = DEFAULT_ACADEMIC): DaySpan | null {
  if (raw === null || raw === '') return null
  if (raw === 'all') return maxDays === undefined ? ALL_TIME_SPAN : null
  if (isRangeAnchor(raw)) {
    const s = anchoredSpan(raw, now, cal)
    return maxDays !== undefined && spanDays(s.from, s.to) > maxDays ? null : s
  }
  const rel = /^(\d{1,4})d$/.exec(raw)
  if (rel) {
    const n = Number(rel[1])
    if (n < 1 || n > MAX_PRESET_DAYS || (maxDays !== undefined && n > maxDays)) return null
    return presetSpan(n, now)
  }
  const abs = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(raw)
  if (abs) {
    let [a, b] = [abs[1] ?? '', abs[2] ?? '']
    if (!validDay(a) || !validDay(b)) return null
    if (a > b) [a, b] = [b, a]
    if (maxDays !== undefined && spanDays(a, b) > maxDays) return null
    return { from: a, to: b }
  }
  return null
}

/** How a default is spelled in the hash, so choosing it again clears the param. */
function defaultSpelling(d: RangeDefault, now: Date, cal: AcademicCalendar): string {
  return isRangeAnchor(d) ? d : encodeRange(defaultSpan(d, now, cal), now)
}

function readHashParam(key: string): string | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  const q = hash.indexOf('?')
  return q === -1 ? null : new URLSearchParams(hash.slice(q + 1)).get(key)
}

function writeHashParam(key: string, value: string | null): void {
  const hash = window.location.hash
  const q = hash.indexOf('?')
  const path = q === -1 ? hash : hash.slice(0, q)
  const params = new URLSearchParams(q === -1 ? '' : hash.slice(q + 1))
  if (value === null) params.delete(key)
  else params.set(key, value)
  const qs = params.toString()
  try {
    window.history.replaceState(window.history.state, '', `${path || '#/'}${qs ? `?${qs}` : ''}`)
  } catch {
    /* a sandboxed frame may refuse; the range still changes */
  }
}

/**
 * The range as React state, mirrored into `?<key>=` with `replaceState` (no
 * history entry, no route change). Feed the pair straight to `DateRangeButton`
 * — its `onChange(from, to, preset)` matches the setter, so an anchored preset
 * is stored as its anchor; send `rangeQuery(range)` to the endpoint.
 *
 * ⚠️ `calendar` IS READ THROUGH A REF. A screen passing `{ yearStartMonth: 6 }`
 * inline would otherwise hand back a new setter every render, and any effect
 * that lists the setter would loop.
 */
export function useHashRange(
  fallback: RangeDefault,
  opts: { key?: string; maxDays?: number; calendar?: AcademicCalendar } = {},
): [DaySpan, (from: string, to: string, preset?: RangePreset) => void] {
  const key = opts.key ?? 'range'
  const maxDays = opts.maxDays
  const calRef = useRef<AcademicCalendar>(opts.calendar ?? DEFAULT_ACADEMIC)
  calRef.current = opts.calendar ?? DEFAULT_ACADEMIC
  const read = useCallback(
    (): DaySpan => decodeRange(readHashParam(key), new Date(), maxDays, calRef.current) ?? defaultSpan(fallback, new Date(), calRef.current),
    [key, maxDays, fallback],
  )
  const [range, setRange] = useState<DaySpan>(read)
  const readRef = useRef(read)
  readRef.current = read

  /* A link to the same route with another `?range=` only changes the hash. */
  useEffect(() => {
    const on = () => {
      const next = readRef.current()
      setRange((cur) => (cur.from === next.from && cur.to === next.to ? cur : next))
    }
    window.addEventListener('hashchange', on)
    return () => {
      window.removeEventListener('hashchange', on)
    }
  }, [])

  const set = useCallback(
    (from: string, to: string, preset?: RangePreset) => {
      const next = { from, to }
      setRange(next)
      const now = new Date()
      const spelled = encodeRange(next, now, preset, calRef.current)
      writeHashParam(key, spelled === defaultSpelling(fallback, now, calRef.current) ? null : spelled)
    },
    [key, fallback],
  )
  return [range, set]
}

/** `1` / `0` → a boolean; anything else (absent, hand-edited) → null. */
export function decodeFlag(raw: string | null): boolean | null {
  return raw === '1' ? true : raw === '0' ? false : null
}

/**
 * A boolean kept in `?<key>=1|0` (the Overview's "Compare with previous N
 * days"), mirrored with `replaceState` like `useHashRange`. Absent means the
 * fallback, and writing the fallback removes the param.
 */
export function useHashFlag(key: string, fallback: boolean): [boolean, (on: boolean) => void] {
  const read = useCallback((): boolean => decodeFlag(readHashParam(key)) ?? fallback, [key, fallback])
  const [flag, setFlag] = useState<boolean>(read)
  const readRef = useRef(read)
  readRef.current = read

  useEffect(() => {
    const on = () => {
      setFlag(readRef.current())
    }
    window.addEventListener('hashchange', on)
    return () => {
      window.removeEventListener('hashchange', on)
    }
  }, [])

  const set = useCallback(
    (on: boolean) => {
      setFlag(on)
      writeHashParam(key, on === fallback ? null : on ? '1' : '0')
    },
    [key, fallback],
  )
  return [flag, set]
}
